import { useEffect, useState } from 'react';

/**
 * PRINT THE SHEET WITH EVERY GROUP OPEN (display P8). A collapsed group renders as a
 * one-liner — its rows are not in the DOM — so print CSS alone cannot "expand" it. The
 * screen holds a `printing` flag: while it is set the sheet is rendered with NO collapsed
 * sections, the print dialog opens on the next frame (the boxes label's own lesson: print
 * in the same tick and the page is captured BEFORE the commit), and `afterprint` puts the
 * person's collapse state back exactly as it was — nothing about their view changes.
 *
 * Ctrl+P / ⌘P goes through `beforeprint`: the flag flips, but browsers do not wait for a
 * React commit before snapshotting, so the keyboard path prints what is on screen — the
 * Print button is the door that guarantees the expansion. Said once here, and in the
 * CHANGELOG, so nobody files "Ctrl+P kept my groups collapsed" as a defect.
 */
export function usePrint(): { printing: boolean; print: () => void } {
  /** The sheet renders expanded. Set by the button AND by beforeprint (Ctrl+P). */
  const [printing, setPrinting] = useState(false);
  /** The BUTTON asked — only this path opens the dialog itself. ⚠️ Kept apart from
   *  `printing` on purpose: if beforeprint also opened the dialog, Ctrl+P would open two. */
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    const before = (): void => setPrinting(true);
    const after = (): void => {
      setPrinting(false);
      setRequested(false);
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);

  useEffect(() => {
    if (!requested) return;
    const raf = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(raf);
  }, [requested]);

  return {
    printing,
    print: () => {
      setPrinting(true);
      setRequested(true);
    },
  };
}
